import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const orgId = searchParams.get('org');
  const serviceId = searchParams.get('service');
  const view = searchParams.get('view') || 'service'; // 'service' or 'calendar'
  
  const [step, setStep] = useState(serviceId ? 2 : 1); // Skip to step 2 if service pre-selected
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgData, setOrgData] = useState(null);
  
  // Booking form
  const [bookingData, setBookingData] = useState({
    date: '',
    time: '',
    partySize: 1,
    selectedAsset: null,
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    addOns: {},
    customQuestions: {}
  });

  useEffect(() => {
    loadData();
  }, [orgId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load services
      const servicesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/services?org_id=${orgId}`);
      const servicesData = await servicesRes.json();
      setServices(servicesData.services || []);
      
      // If service pre-selected, load it
      if (serviceId) {
        const service = servicesData.services?.find(s => s.id === serviceId);
        if (service) {
          setSelectedService(service);
        }
      }
      
      // Load org data
      const orgRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/organization?org_id=${orgId}`);
      const orgDataRes = await orgRes.json();
      setOrgData(orgDataRes);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load booking data');
    } finally {
      setLoading(false);
    }
  };

  const selectService = (service) => {
    setSelectedService(service);
    setStep(2);
  };

  const updateBookingData = (field, value) => {
    setBookingData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8 text-center">
          {orgData?.logo ? (
            <img src={orgData.logo} alt={orgData.name} className="h-12 sm:h-16 mx-auto mb-4" />
          ) : (
            <h1 className="text-2xl sm:text-4xl font-bold mb-2">{orgData?.name || 'Book Now'}</h1>
          )}
        </div>

        {/* Step 1: Service Selection */}
        {step === 1 && (
          <div>
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-center">Select a Service</h2>
            
            {services.length === 0 ? (
              <div className="text-center py-12 sm:py-16">
                <div className="text-5xl sm:text-6xl mb-4">📦</div>
                <p className="text-gray-600">No services available</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {services.filter(s => s.active).map((service) => (
                  <div
                    key={service.id}
                    onClick={() => selectService(service)}
                    className="bg-white rounded-xl overflow-hidden cursor-pointer hover:shadow-xl transition-all border-2 border-transparent hover:border-blue-500"
                  >
                    {/* Service Image */}
                    <div className="relative h-48 sm:h-56 bg-gray-100">
                      {service.images && service.images.length > 0 ? (
                        <img
                          src={service.images[service.mainImageIndex || 0]}
                          alt={service.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-5xl">🖼️</div>
                      )}
                    </div>
                    
                    {/* Service Info */}
                    <div className="p-4 sm:p-5">
                      <h3 className="font-bold text-lg sm:text-xl mb-1">{service.name}</h3>
                      {service.tagline && (
                        <p className="text-sm text-gray-600 mb-3">{service.tagline}</p>
                      )}
                      {service.description && (
                        <p className="text-sm text-gray-500 line-clamp-2 mb-4">
                          {service.description}
                        </p>
                      )}
                      
                      <div className="flex items-baseline gap-3 mb-4">
                        {service.pricingModel === 'duration_based' && service.durationOptions ? (
                          <>
                            <span className="text-sm text-gray-600">Starting at</span>
                            <span className="text-xl sm:text-2xl font-bold text-blue-600">
                              ${Math.min(...service.durationOptions.filter(d => d.enabled).map(d => d.price))}
                            </span>
                          </>
                        ) : (
                          <span className="text-xl sm:text-2xl font-bold text-blue-600">
                            ${service.price}
                          </span>
                        )}
                        {service.duration && (
                          <span className="text-sm text-gray-600">• {service.duration} min</span>
                        )}
                      </div>
                      
                      <Button className="w-full bg-blue-600 hover:bg-blue-700">
                        Select
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Service Details & Booking */}
        {step === 2 && selectedService && (
          <ServiceBookingFlow
            service={selectedService}
            orgData={orgData}
            onBack={() => {
              setStep(1);
              setSelectedService(null);
            }}
            bookingData={bookingData}
            updateBookingData={updateBookingData}
          />
        )}
      </div>
    </div>
  );
};

// Service Booking Flow Component
const ServiceBookingFlow = ({ service, orgData, onBack, bookingData, updateBookingData }) => {
  const [bookingStep, setBookingStep] = useState(1); // 1=Date/Time, 2=Customer Info, 3=Review
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(false);

  // Check availability when date changes
  useEffect(() => {
    if (bookingData.date) {
      checkAvailability();
    }
  }, [bookingData.date]);

  const checkAvailability = async () => {
    setLoadingTimes(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/public/services/${service.id}/availability?date=${bookingData.date}&party_size=${bookingData.partySize}`
      );
      const data = await response.json();
      setAvailableTimes(data.availableTimes || []);
    } catch (error) {
      console.error('Error checking availability:', error);
    } finally {
      setLoadingTimes(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
      {/* Left: Service Details (Sticky on desktop) */}
      <div className="lg:col-span-1">
        <div className="bg-white rounded-xl overflow-hidden sticky top-4">
          {/* Back Button */}
          <button
            onClick={onBack}
            className="w-full p-3 text-left text-sm text-gray-600 hover:bg-gray-50 border-b"
          >
            ← Back to services
          </button>
          
          {/* Service Image */}
          <div className="relative h-48 bg-gray-100">
            {service.images && service.images.length > 0 ? (
              <img
                src={service.images[service.mainImageIndex || 0]}
                alt={service.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-5xl">🖼️</div>
            )}
          </div>
          
          {/* Service Info */}
          <div className="p-4 sm:p-5">
            <h2 className="text-xl sm:text-2xl font-bold mb-2">{service.name}</h2>
            {service.tagline && (
              <p className="text-sm text-gray-600 mb-3">{service.tagline}</p>
            )}
            
            <div className="text-2xl font-bold text-blue-600 mb-4">
              ${service.price}
            </div>
            
            {/* What's Included */}
            {service.whatsIncluded && service.whatsIncluded.length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold text-sm mb-2">What's Included</h3>
                <ul className="space-y-1">
                  {service.whatsIncluded.map((item, i) => (
                    <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-green-600">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Custom Sections */}
            {service.customSections && service.customSections.map((section, i) => (
              <div key={i} className="mb-4">
                <h3 className="font-semibold text-sm mb-2">{section.title}</h3>
                <p className="text-sm text-gray-600 whitespace-pre-line">{section.content}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Booking Form */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-xl p-4 sm:p-6">
          <h2 className="text-xl sm:text-2xl font-bold mb-6">Book Your Experience</h2>
          
          {/* Step 1: Date & Time */}
          {bookingStep === 1 && (
            <div className="space-y-6">
              <div>
                <Label className="text-base">Select Date *</Label>
                <Input
                  type="date"
                  value={bookingData.date}
                  onChange={(e) => updateBookingData('date', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="mt-2"
                />
              </div>

              <div>
                <Label className="text-base">Party Size *</Label>
                <select
                  value={bookingData.partySize}
                  onChange={(e) => updateBookingData('partySize', parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg mt-2"
                >
                  {Array.from({ length: Math.min(service.capacity || 20, 50) }, (_, i) => i + 1).map(num => (
                    <option key={num} value={num}>{num} {num === 1 ? 'person' : 'people'}</option>
                  ))}
                </select>
              </div>

              {bookingData.date && (
                <div>
                  <Label className="text-base">Select Time *</Label>
                  {loadingTimes ? (
                    <div className="text-center py-8">
                      <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                    </div>
                  ) : availableTimes.length === 0 ? (
                    <p className="text-sm text-gray-500 mt-2">No times available for this date</p>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                      {availableTimes.map((time) => (
                        <button
                          key={time}
                          onClick={() => updateBookingData('time', time)}
                          className={`py-3 px-4 border-2 rounded-lg font-medium transition-all ${
                            bookingData.time === time
                              ? 'border-blue-600 bg-blue-50 text-blue-600'
                              : 'border-gray-200 hover:border-blue-300'
                          }`}
                        >
                          {time}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {bookingData.date && bookingData.time && (
                <Button
                  onClick={() => setBookingStep(2)}
                  className="w-full bg-blue-600 hover:bg-blue-700 py-4 text-base"
                >
                  Continue
                </Button>
              )}
            </div>
          )}

          {/* Step 2: Customer Info & Add-ons */}
          {bookingStep === 2 && (
            <div className="space-y-6">
              <button
                onClick={() => setBookingStep(1)}
                className="text-sm text-gray-600 hover:text-gray-900 mb-4"
              >
                ← Change date/time
              </button>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-medium">
                  {new Date(bookingData.date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'long', 
                    day: 'numeric',
                    year: 'numeric'
                  })} at {bookingData.time}
                </p>
                <p className="text-sm text-gray-600">{bookingData.partySize} {bookingData.partySize === 1 ? 'person' : 'people'}</p>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-4">Customer Information</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input
                      value={bookingData.customerName}
                      onChange={(e) => updateBookingData('customerName', e.target.value)}
                      placeholder="John Doe"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={bookingData.customerEmail}
                      onChange={(e) => updateBookingData('customerEmail', e.target.value)}
                      placeholder="john@example.com"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>Phone *</Label>
                    <Input
                      type="tel"
                      value={bookingData.customerPhone}
                      onChange={(e) => updateBookingData('customerPhone', e.target.value)}
                      placeholder="5551234567"
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* SMS Consent Checkbox */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={bookingData.smsConsent || false}
                      onChange={(e) => updateBookingData('smsConsent', e.target.checked)}
                      className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      data-testid="sms-consent-checkbox"
                    />
                    <span className="text-sm text-gray-700">
                      <strong className="font-semibold">I confirm this contact information is correct. I'll receive my booking confirmation via email and text.</strong>
                      <br />
                      <span className="text-xs text-gray-600">
                        By checking this box, I consent to receive SMS messages. Msg frequency varies. Msg & data rates may apply. Reply STOP to opt out, HELP for help.{' '}
                        <a href="/privacy#sms-terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              {/* Add-ons */}
              {service.addOns && service.addOns.length > 0 && (
                <div>
                  <h3 className="font-semibold text-lg mb-4">Enhance Your Booking</h3>
                  <div className="space-y-4">
                    {service.addOns.map((addOn, index) => (
                      <div key={index} className="border rounded-lg p-4">
                        {addOn.type === 'checkbox' && (
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              id={`addon-${index}`}
                              onChange={(e) => {
                                const newAddOns = { ...bookingData.addOns };
                                newAddOns[addOn.title] = e.target.checked ? addOn.price : 0;
                                updateBookingData('addOns', newAddOns);
                              }}
                              className="w-5 h-5 mt-0.5"
                            />
                            <label htmlFor={`addon-${index}`} className="flex-1 cursor-pointer">
                              <div className="font-semibold">{addOn.title}</div>
                              {addOn.description && (
                                <div className="text-sm text-gray-600 mt-1">{addOn.description}</div>
                              )}
                              <div className="text-blue-600 font-semibold mt-2">+${addOn.price}</div>
                            </label>
                          </div>
                        )}

                        {addOn.type === 'dropdown' && (
                          <div>
                            <Label>{addOn.title} {addOn.required && '*'}</Label>
                            {addOn.description && (
                              <p className="text-sm text-gray-600 mb-2">{addOn.description}</p>
                            )}
                            <select
                              onChange={(e) => {
                                const selectedOption = addOn.options.find(opt => opt.label === e.target.value);
                                const newAddOns = { ...bookingData.addOns };
                                newAddOns[addOn.title] = selectedOption?.price || 0;
                                updateBookingData('addOns', newAddOns);
                              }}
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg mt-1"
                            >
                              <option value="">Choose an option</option>
                              {addOn.options.map((opt, i) => (
                                <option key={i} value={opt.label}>
                                  {opt.label} {opt.price > 0 ? `(+$${opt.price})` : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        {addOn.type === 'informational' && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="font-semibold text-blue-900 mb-1">{addOn.title}</div>
                            <div className="text-sm text-blue-800">{addOn.description}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Policies */}
              {(service.customCancellationPolicy || service.disclaimers) && (
                <div className="border-t pt-6">
                  {service.customCancellationPolicy && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-sm mb-2">Cancellation Policy</h4>
                      <p className="text-sm text-gray-600 whitespace-pre-line">{service.customCancellationPolicy}</p>
                    </div>
                  )}
                  
                  {service.disclaimers && (
                    <div>
                      <h4 className="font-semibold text-sm mb-2">Important Information</h4>
                      <p className="text-sm text-gray-600 whitespace-pre-line">{service.disclaimers}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-4">
                <Button
                  onClick={() => setBookingStep(3)}
                  disabled={!bookingData.customerName || !bookingData.customerEmail || !bookingData.customerPhone || !bookingData.smsConsent}
                  className="w-full bg-blue-600 hover:bg-blue-700 py-4 text-base"
                >
                  Review Booking
                </Button>

                <p className="text-xs text-center text-gray-500 mt-3">
                  By continuing, you agree to the{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Privacy Policy</a>
                  {' '}and{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Terms of Service</a>
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Review & Payment */}
          {bookingStep === 3 && (
            <div className="space-y-6">
              <button
                onClick={() => setBookingStep(2)}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                ← Edit details
              </button>

              <div>
                <h3 className="font-semibold text-lg mb-4">Review Your Booking</h3>
                
                {/* Booking Summary */}
                <div className="space-y-3 bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Service</span>
                    <span className="font-semibold">{service.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Date</span>
                    <span className="font-semibold">
                      {new Date(bookingData.date).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Time</span>
                    <span className="font-semibold">{bookingData.time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Party Size</span>
                    <span className="font-semibold">{bookingData.partySize} {bookingData.partySize === 1 ? 'person' : 'people'}</span>
                  </div>
                  
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between text-lg">
                      <span className="font-semibold">Total</span>
                      <span className="font-bold text-blue-600">
                        ${service.price + Object.values(bookingData.addOns).reduce((sum, price) => sum + price, 0)}
                      </span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={() => {
                    toast.success('Booking functionality coming soon!');
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 py-4 text-base"
                >
                  Complete Booking
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
